import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Snackbar, Alert, ThemeProvider } from '@mui/material'
import { theme } from '../../theme'

/**
 * Centralized toast/notification system.
 *
 * A single <ToastProvider> is mounted app-wide (see gatsby-browser.js /
 * gatsby-ssr.js). Any component can call the `useToast()` hook to surface a
 * success / error / info / warning message without wiring up its own Snackbar.
 *
 * The page theme is applied per-route rather than at the root, so the Snackbar
 * is wrapped in its own <ThemeProvider> to guarantee consistent colours even
 * when a toast fires from a page that has not mounted the theme itself.
 *
 * Toasts are queued: a message arriving while another is on screen closes the
 * current one and is shown after its exit transition, rather than replacing it
 * mid-animation.
 */

const ToastContext = createContext(null)

const AUTO_HIDE_MS = 5000

// Hoisted so the fail-soft branch of useToast() returns a stable reference and
// is safe to use as a hook dependency.
const noop = () => {}
const NOOP_TOAST = {
  show: noop,
  success: noop,
  error: noop,
  info: noop,
  warning: noop,
}

export function ToastProvider({ children }) {
  const [current, setCurrent] = useState(null)
  const [open, setOpen] = useState(false)

  const queueRef = useRef([])
  const openRef = useRef(false)
  const seqRef = useRef(0)

  const applyOpen = useCallback((next) => {
    openRef.current = next
    setOpen(next)
  }, [])

  const processQueue = useCallback(() => {
    if (queueRef.current.length === 0) return
    setCurrent(queueRef.current.shift())
    applyOpen(true)
  }, [applyOpen])

  const show = useCallback(
    (message, severity = 'info') => {
      if (!message) return
      seqRef.current += 1
      queueRef.current.push({ message, severity, key: seqRef.current })

      if (openRef.current) {
        applyOpen(false)
      } else {
        processQueue()
      }
    },
    [applyOpen, processQueue]
  )

  const handleClose = useCallback(
    (_event, reason) => {
      if (reason === 'clickaway') return
      applyOpen(false)
    },
    [applyOpen]
  )

  const handleExited = useCallback(() => {
    setCurrent(null)
    processQueue()
  }, [processQueue])

  const api = useMemo(
    () => ({
      show,
      success: (message) => show(message, 'success'),
      error: (message) => show(message, 'error'),
      info: (message) => show(message, 'info'),
      warning: (message) => show(message, 'warning'),
    }),
    [show]
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ThemeProvider theme={theme}>
        <Snackbar
          key={current?.key}
          open={open}
          autoHideDuration={AUTO_HIDE_MS}
          onClose={handleClose}
          TransitionProps={{ onExited: handleExited }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleClose}
            severity={current?.severity || 'info'}
            variant="filled"
            elevation={6}
            sx={{
              width: '100%',
              borderRadius: '12px',
              alignItems: 'center',
              fontWeight: 500,
            }}
          >
            {current?.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  return ctx || NOOP_TOAST
}

export default ToastProvider
